"use client";

import Image from "next/image";
import React, { useState, useRef } from "react";

const fallbackEvents = [
  {
    name: "Today",
    title: "Diwali Lights ✨",
    cover: "/images/lifeTycho/diwali.webp",
    photos: ["/images/lifeTycho/diwali.webp", "/images/lifeTycho/diwali1.webp"],
  },
  {
    name: "This week",
    title: "Mountain trip 🏔️",
    cover: "/images/lifeTycho/gallery-3.webp",
    photos: ["/images/lifeTycho/gallery-3.webp"],
  },
  {
    name: "Friday vibe",
    title: "Team party 🎉",
    cover: "/images/lifeTycho/gallery-11.webp",
    photos: ["/images/lifeTycho/gallery-11.webp"],
  },
  {
    name: "Yesterday",
    title: "Birthday bash 🎂",
    cover: "/images/lifeTycho/gallery-29.webp",
    photos: ["/images/lifeTycho/gallery-29.webp"],
  },
];

const closeBtnStyle = {
  position: "absolute",
  top: "24px",
  right: "24px",
  background: "rgba(255,255,255,0.15)",
  border: "2px solid rgba(255,255,255,0.5)",
  color: "white",
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  fontSize: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 100000,
  flexShrink: 0,
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const TychoEvents = ({ events }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeftVal = useRef(0);
  const isDragging = useRef(false);

  const activeEvents = events ? Object.entries(events).filter(([_, eventData]) => eventData.status === "active" && eventData.photos?.length) : [];

  const activeEventsList = activeEvents.map(([eventName, eventData], index) => {
    const tags = ["Today", "This week", "Friday vibe", "Yesterday", "Last month", "Memories"];
    return {
      name: tags[index % tags.length],
      title: eventName.replace(/([A-Z])/g, " $1").trim(),
      cover: eventData.photos?.[0]?.url || "/images/lifeTycho/gallery-1.webp",
      photos: eventData.photos?.map((p) => p.url) || [],
    };
  });

  const displayEvents = activeEventsList.length > 0 ? activeEventsList : fallbackEvents;

  const openEvent = (evt) => {
    setImages(evt.photos);
    setCurrentIndex(0);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setImages([]);
    setCurrentIndex(0);
  };

  const next = () => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const prev = () => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e) => {
    isDown.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftVal.current = scrollRef.current.scrollLeft;
    isDragging.current = false;
    scrollRef.current.style.cursor = "grabbing";
    scrollRef.current.style.scrollBehavior = "auto";
  };

  const handleMouseLeave = () => {
    if (isDown.current) {
      isDown.current = false;
      scrollRef.current.style.cursor = "grab";
      scrollRef.current.style.scrollBehavior = "smooth";
    }
  };

  const handleMouseUp = () => {
    if (isDown.current) {
      isDown.current = false;
      scrollRef.current.style.cursor = "grab";
      scrollRef.current.style.scrollBehavior = "smooth";
      if (isDragging.current) {
        setTimeout(() => {
          isDragging.current = false;
        }, 50);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDown.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5; // scroll speed multiplier
    if (Math.abs(x - startX.current) > 10) {
      isDragging.current = true;
    }
    scrollRef.current.scrollLeft = scrollLeftVal.current - walk;
  };

  return (
    <>
      <section className="w-full border-t border-b border-border-primary py-16! md:py-2! px-6! md:px-12! 2xl:py-2! 2xl:px-24! flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16 2xl:gap-24 bg-transparent box-border font-sans">
        <div className="w-full lg:w-[280px] xl:w-[320px] 2xl:w-[300px] shrink-0 flex flex-col gap-3 2xl:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-[4px] h-[28px] bg-[#7c3aed] rounded-sm shrink-0" />
            <h2 className="text-text-primary text-3xl md:text-[30px] 2xl:text-[36px] font-bold leading-[1.2] m-0!">Life at Tycho</h2>
          </div>
          <p className="text-text-secondary text-xs md:text-[13px] 2xl:text-[15px] leading-relaxed m-0! ml-4!">Where work feels like home</p>
        </div>

        <div className="flex-1 min-w-0 relative flex items-center w-full">
          <div
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar w-full py-2! cursor-grab select-none"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {displayEvents.map((evt, idx) => (
              <div
                key={idx}
                onClick={() => {
                  if (isDragging.current) return;
                  openEvent(evt);
                }}
                className="group relative shrink-0 w-[220px] sm:w-[240px] 2xl:w-[280px] h-[160px] 2xl:h-[180px] rounded-[12px] overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] "
              >
                <Image src={evt.cover} alt={evt.title} fill draggable="false" className="object-cover transition-transform duration-500 group-hover:scale-110" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-5! flex flex-col items-start gap-1">
                  <span className="text-base md:text-lg 2xl:text-xl font-bold text-white leading-tight m-0!">{evt.title}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={scrollRight}
            className={`absolute -right-4! z-10 w-12 h-12 rounded-full bg-surface-primary text-text-primary border border-border-primary flex items-center justify-center shadow-lg hover:scale-105 hover:bg-surface-secondary active:scale-95 transition-all cursor-pointer ${
              displayEvents.length <= 1 ? "hidden!" : ""
            } ${displayEvents.length <= 2 ? "md:hidden!" : ""} ${displayEvents.length <= 3 ? "lg:hidden!" : ""} ${displayEvents.length <= 3 ? "2xl:hidden!" : ""}`}
          >
            <span className="text-xl font-bold">→</span>
          </button>
        </div>
      </section>

      {isOpen && (
        <div style={{ ...overlayStyle, background: "rgba(0,0,0,0.95)" }}>
          <button onClick={close} style={closeBtnStyle}>
            ✕
          </button>

          <button
            onClick={prev}
            style={{
              position: "absolute",
              left: "24px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "white",
              fontSize: "52px",
              cursor: "pointer",
              zIndex: 100000,
              lineHeight: 1,
            }}
          >
            ‹
          </button>

          <Image
            src={images[currentIndex]}
            alt="Event Detail"
            width={1200}
            height={800}
            style={{
              display: "block",
              maxWidth: "100vw",
              maxHeight: "100vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
          <button
            onClick={next}
            style={{
              position: "absolute",
              right: "24px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "white",
              fontSize: "52px",
              cursor: "pointer",
              zIndex: 100000,
              lineHeight: 1,
            }}
          >
            ›
          </button>

          <div
            style={{
              position: "absolute",
              bottom: "24px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "rgba(255,255,255,0.6)",
              fontSize: "14px",
            }}
          >
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default TychoEvents;
